import type { CommerceHttpAdapter } from '../../client/CommerceHttpAdapter.js'
import type { CacheStrategy } from '../../workers/CacheStrategy.js'
import type { StoreWithOpeningHours } from '../../types/index.js'

export class StoresModule {
  constructor(
    private readonly http: CommerceHttpAdapter,
    private readonly cache: CacheStrategy
  ) {}

  /** Recherche des magasins par coordonnées géographiques */
  findByLocation(latitude: number, longitude: number, countryCode = 'FRA', count = 10, feature?: string): Promise<StoreWithOpeningHours[]> {
    const country = countryCode.toUpperCase()
    let url = `/commerce/api/stores/locator/by-proximity/${country}?latitude=${latitude}&longitude=${longitude}&count=${count}`
    if (feature) url += `&feature=${encodeURIComponent(feature)}`
    const key = `stores:geo:${country}:${latitude.toFixed(4)}:${longitude.toFixed(4)}:${count}:${feature ?? ''}`
    return this.cache.execute(key, () => this.http.get<StoreWithOpeningHours[]>(url), 'cache-first', 5 * 60_000)
  }

  /** Recherche des magasins par code postal */
  findByPostalCode(postalCode: string, countryCode = 'FRA', count = 10, feature?: string): Promise<StoreWithOpeningHours[]> {
    const country = countryCode.toUpperCase()
    let url = `/commerce/api/stores/locator/by-proximity/${country}/${encodeURIComponent(postalCode)}?count=${count}`
    if (feature) url += `&feature=${encodeURIComponent(feature)}`
    const key = `stores:postal:${country}:${postalCode}:${count}:${feature ?? ''}`
    return this.cache.execute(key, () => this.http.get<StoreWithOpeningHours[]>(url), 'cache-first', 15 * 60_000)
  }

  /** Récupère le détail d'un magasin par son GUID */
  getStore(storeGuid: string): Promise<StoreWithOpeningHours> {
    return this.cache.execute(
      `stores:detail:${storeGuid}`,
      () => this.http.get<StoreWithOpeningHours>(`/commerce/api/stores/locator/${storeGuid}`),
      'stale-while-revalidate'
    )
  }

  /** Récupère le magasin associé à la session en cours (null si aucun) */
  async getSessionStore(): Promise<StoreWithOpeningHours | null> {
    try {
      return await this.http.get<StoreWithOpeningHours>('/commerce/api/stores/locator/from-session')
    } catch {
      return null
    }
  }
}
